import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { GALLERY, VIDEOS, IMAGES } from "@/lib/content";
import { PhotoStories } from "@/components/PhotoStories";

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page">
      <PageHeader
        overline="Moments in the water"
        title="The Mimi & Me"
        italic="gallery."
        subtitle="Watch real lessons in action and browse progress moments from Boca Raton's beautiful home pools. Tap any photo to view it up close."
      />

      {/* VIDEOS */}
      <section className="py-12" data-testid="gallery-videos">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Watch</p>
            <h2 className="font-display text-4xl md:text-5xl mb-10" style={{ color: "var(--navy)" }}>Lessons &amp; moments in motion</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {VIDEOS.map((v, i) => (
              <Reveal key={i} delay={(i % 2) * 0.1}>
                <div className="rounded-2xl overflow-hidden border bg-white" style={{ borderColor: "rgba(212,175,55,0.3)" }} data-testid={`gallery-video-${i}`}>
                  <video className="w-full aspect-video object-cover bg-black" src={v.src} poster={v.poster} controls playsInline preload="metadata" />
                  <div className="p-5">
                    <h3 className="font-display text-2xl" style={{ color: "var(--navy)" }}>{v.title}</h3>
                    <p className="font-body font-light text-sm mt-1" style={{ color: "var(--muted)" }}>{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PhotoStories />

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Photos</p>
            <h2 className="font-display text-4xl md:text-5xl mb-10" style={{ color: "var(--navy)" }}>Progress, up close</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[240px] gap-4">
            {GALLERY.map((g, i) => (
              <Reveal key={i} delay={(i % 4) * 0.08} className={g.span}>
                <button
                  onClick={() => setActive(g)}
                  className="group relative w-full h-full overflow-hidden rounded-2xl border cursor-pointer block"
                  style={{ borderColor: "rgba(212,175,55,0.25)" }}
                  data-testid={`gallery-item-${i}`}
                >
                  <img src={g.src} alt={g.label} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                  <div className="absolute inset-0 flex items-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(180deg, transparent, rgba(11,27,61,0.82))" }}>
                    <span className="font-display text-xl" style={{ color: "var(--cream)" }}>{g.label}</span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="font-body font-light text-center mt-12 text-sm" style={{ color: "var(--muted)" }}>
              Real lesson photos & underwater progress videos are shared with every family.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SUNNY MOMENTS BANNER */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border shadow-[0_20px_60px_rgba(11,27,61,0.15)]" style={{ borderColor: "rgba(212,175,55,0.4)" }}>
              <img src={IMAGES.sunnyMoments} alt="Sunny Moments — memories that last a lifetime" className="w-full h-auto block" />
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-10"
            style={{ backgroundColor: "rgba(11,27,61,0.92)" }}
            data-testid="gallery-lightbox"
          >
            <button onClick={() => setActive(null)} className="absolute top-6 right-6 p-2" style={{ color: "var(--cream)" }} data-testid="lightbox-close">
              <X size={30} />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-[92vw] max-h-[86vh]"
            >
              <img src={active.src} alt={active.label} className="max-w-[92vw] max-h-[80vh] object-contain rounded-2xl border" style={{ borderColor: "rgba(212,175,55,0.4)" }} />
              <p className="font-display text-2xl text-center mt-4" style={{ color: "var(--cream)" }}>{active.label}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
